import React, { useState, useMemo } from 'react'
import SearchBar from '../components/SearchBar'
import CompanyCard from '../components/CompanyCard'
import SEOHead, { seoConfigs } from '../components/SEOHead'
import aiCompaniesData from '../data/aiCompanies.json'

const CompaniesPage = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedExpertise, setSelectedExpertise] = useState('All')
  const [isFilterExpanded, setIsFilterExpanded] = useState(() => {
    // Default to expanded on desktop (>= 640px), collapsed on mobile
    return typeof window !== 'undefined' ? window.innerWidth >= 640 : true
  })

  // Get unique expertise areas
  const expertiseAreas = useMemo(() => {
    const allExpertise = aiCompaniesData.flatMap(company => company.expertise)
    const uniqueExpertise = [...new Set(allExpertise)]
    return ['All', ...uniqueExpertise.sort()]
  }, [])

  // Filter companies based on search term and expertise
  const filteredCompanies = useMemo(() => {
    return aiCompaniesData.filter(company => {
      const matchesSearch = searchTerm === '' || 
        company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.headquarters.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.expertise.some(area => area.toLowerCase().includes(searchTerm.toLowerCase())) ||
        company.famousProducts.some(product => product.toLowerCase().includes(searchTerm.toLowerCase()))

      const matchesExpertise = selectedExpertise === 'All' || 
        company.expertise.some(area => area === selectedExpertise)

      return matchesSearch && matchesExpertise
    })
  }, [searchTerm, selectedExpertise])

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
          AI Companies
        </h1>
        <p className="text-xl text-white/80 max-w-3xl mx-auto">
          Explore the leading companies shaping the future of artificial intelligence. From research labs to commercial enterprises, discover the organizations driving AI innovation.
        </p>
        <div className="flex items-center justify-center space-x-4 text-white/60">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
            <span>{aiCompaniesData.length} Companies</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
            <span>{expertiseAreas.length - 1} Expertise Areas</span>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <SearchBar 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        placeholder="Search companies by name, location, expertise, or products..."
      />

      {/* Expertise Filter */}
      <div className="glass-card rounded-2xl p-4 sm:p-6">
        <button
          onClick={() => setIsFilterExpanded(!isFilterExpanded)}
          className="w-full flex items-center justify-between text-white font-semibold mb-4 focus:outline-none focus:ring-2 focus:ring-white/50 rounded-lg p-2 hover:bg-white/10 transition-all duration-300"
          aria-expanded={isFilterExpanded}
          aria-controls="expertise-filter-content"
        >
          <span className="flex items-center space-x-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span>Filter by Expertise Area</span>
            {selectedExpertise !== 'All' && (
              <span className="bg-purple-500 text-white text-xs px-2 py-1 rounded-full">
                {selectedExpertise}
              </span>
            )}
          </span>
          <svg 
            className={`w-5 h-5 transition-transform duration-300 ${isFilterExpanded ? 'rotate-180' : ''}`} 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        <div 
          id="expertise-filter-content"
          className={`transition-all duration-300 overflow-hidden ${
            isFilterExpanded 
              ? 'max-h-96 opacity-100' 
              : 'max-h-0 opacity-0'
          }`}
        >
          <div className="max-h-64 sm:max-h-none overflow-y-auto">
            <div className="flex flex-wrap gap-2 pr-2">
              {expertiseAreas.map(expertise => (
                <button
                  key={expertise}
                  onClick={() => {
                    setSelectedExpertise(expertise)
                    // Auto-collapse on mobile after selection
                    if (window.innerWidth < 640) {
                      setIsFilterExpanded(false)
                    }
                  }}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/50 ${
                    selectedExpertise === expertise
                      ? 'bg-purple-500 text-white shadow-lg'
                      : 'bg-white/10 text-white/80 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  {expertise}
                  {expertise !== 'All' && (
                    <span className="ml-2 text-xs opacity-75">
                      ({aiCompaniesData.filter(company => 
                        company.expertise.includes(expertise)
                      ).length})
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Results Count */}
      {searchTerm && (
        <div className="text-center">
          <p className="text-white/80">
            Found {filteredCompanies.length} compan{filteredCompanies.length !== 1 ? 'ies' : 'y'} 
            {searchTerm && ` matching "${searchTerm}"`}
          </p>
        </div>
      )}

      {/* Companies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCompanies.map((company, index) => (
          <div key={company.id} style={{ animationDelay: `${index * 0.1}s` }}>
            <CompanyCard company={company} />
          </div>
        ))}
      </div>

      {/* No Results */}
      {filteredCompanies.length === 0 && (
        <div className="text-center py-12">
          <div className="glass-card rounded-2xl p-8 max-w-md mx-auto">
            <div className="w-16 h-16 bg-gradient-to-r from-gray-500 to-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No companies found</h3>
            <p className="text-white/70 mb-4">
              Try adjusting your search terms or expertise filter.
            </p>
            <button
              onClick={() => {
                setSearchTerm('')
                setSelectedExpertise('All')
              }}
              className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Call to Action 
      {filteredCompanies.length > 0 && (
        <div className="text-center py-8">
          <div className="glass-card rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">
              Want to add your company?
            </h3>
            <p className="text-white/80 mb-6">
              Join our directory of innovative AI companies and showcase your expertise to the community.
            </p>
            <button className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl">
              Submit Your Company
            </button>
          </div>
        </div>
      )}*/}
    </div>
  )
}

export default CompaniesPage
